import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Download, ExternalLink, Printer, ZoomIn, ZoomOut, RefreshCcw,
} from "lucide-react";
import { portalApi } from "../../context/PortalAuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

export function fmtCurrency(amount, currency = "BRL") {
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(Number(amount || 0));
  } catch {
    return `${currency} ${Number(amount || 0).toFixed(2)}`;
  }
}

// pt-BR labels for enum values coming from the backend (kept in sync with
// _build_receipt_pdf's method_labels/status_labels).
const PAYMENT_METHOD_LABELS = {
  pix: "PIX",
  cash: "Dinheiro",
  credit_card: "Cartão de Crédito",
  debit_card: "Cartão de Débito",
  transfer: "Transferência",
  bank_transfer: "Transferência Bancária",
  boleto: "Boleto",
  cheque: "Cheque",
  other: "Outro",
};

const PAYMENT_STATUS_LABELS = {
  paid: "Pago",
  issued: "Emitido",
  pending: "Pendente",
  partial: "Parcial",
  refunded: "Reembolsado",
  cancelled: "Cancelado",
};

export function labelPaymentMethod(m) {
  if (!m) return "—";
  return PAYMENT_METHOD_LABELS[String(m).toLowerCase()] || String(m).replace(/_/g, " ");
}

export function labelPaymentStatus(s) {
  if (!s) return "—";
  return PAYMENT_STATUS_LABELS[String(s).toLowerCase()] || String(s).replace(/_/g, " ");
}

/** Fetch a receipt file as a blob URL using the portal Bearer token. */
export async function fetchReceiptBlobUrl(receiptId) {
  const res = await portalApi.get(`/portal/receipts/${receiptId}/file`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

/** Trigger a browser download for a portal receipt. */
export async function downloadReceipt(receipt) {
  try {
    const res = await portalApi.get(`/portal/receipts/${receipt.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${receipt.receipt_number || receipt.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("Download iniciado");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Não foi possível baixar o recibo.");
  }
}

/** Send print-tracking to the backend (fire-and-forget). */
export async function trackPrintReceipt(receiptId) {
  try { await portalApi.post(`/portal/receipts/${receiptId}/track-print`); } catch { /* silent */ }
}

/** Modal PDF viewer with zoom, print and open-in-new-tab. */
export function PortalReceiptViewer({ receipt, open, onClose, showAmounts = true }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(1);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!open || !receipt) return;
    let alive = true;
    let currentUrl = "";
    setLoading(true); setError(""); setBlobUrl(""); setZoom(1);
    fetchReceiptBlobUrl(receipt.id)
      .then((url) => { if (!alive) { URL.revokeObjectURL(url); return; } currentUrl = url; setBlobUrl(url); })
      .catch((e) => alive && setError(e?.response?.data?.detail || "Não foi possível carregar o recibo."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [open, receipt]);

  const openInNewTab = () => {
    if (!blobUrl) return;
    const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!w) toast.error("O navegador bloqueou a nova aba. Permita popups para este site.");
  };

  const doPrint = async () => {
    if (!blobUrl || !receipt) return;
    trackPrintReceipt(receipt.id);
    try {
      const win = iframeRef.current?.contentWindow;
      if (win) { win.focus(); win.print(); toast.success("Enviado para impressão"); return; }
    } catch (_) { /* fall through */ }
    // Fallback: open the PDF in a new tab and let the user use browser print.
    const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (w) toast("Use Ctrl+P na nova aba para imprimir.");
    else toast.error("Não foi possível abrir a janela de impressão.");
  };

  if (!receipt) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 overflow-hidden"
        data-testid="portal-receipt-viewer"
      >
        <DialogHeader className="px-5 py-3 border-b border-border">
          <DialogTitle className="text-base font-semibold truncate pr-8">
            Recibo · <span className="font-mono">{receipt.receipt_number}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pré-visualização do recibo em PDF. Use os botões abaixo para imprimir, baixar ou abrir em nova aba.
          </DialogDescription>
          <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-muted-foreground">
            <Badge variant="outline" className="text-[10px]">{labelPaymentStatus(receipt.payment_status)}</Badge>
            {receipt.process_number && <span className="font-mono">{receipt.process_number}</span>}
            {receipt.service_name && <span>· {receipt.service_name}</span>}
            <span>· {fmtDateTime(receipt.payment_date || receipt.created_at)}</span>
            {showAmounts && <span>· {fmtCurrency(receipt.amount, receipt.currency)}</span>}
            {receipt.payment_method && <span>· {labelPaymentMethod(receipt.payment_method)}</span>}
          </div>
        </DialogHeader>

        {/* Zoom & action bar */}
        <div className="flex items-center gap-2 px-5 py-2 border-b border-border bg-muted/30">
          <Button
            variant="ghost" size="sm"
            onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.15).toFixed(2)))}
            data-testid="portal-receipt-zoom-out"
            disabled={!blobUrl || !!error}
            title="Diminuir zoom"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs tabular-nums text-muted-foreground w-14 text-center" data-testid="portal-receipt-zoom-level">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost" size="sm"
            onClick={() => setZoom((z) => Math.min(3, +(z + 0.15).toFixed(2)))}
            data-testid="portal-receipt-zoom-in"
            disabled={!blobUrl || !!error}
            title="Aumentar zoom"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => setZoom(1)}
            data-testid="portal-receipt-zoom-reset"
            disabled={!blobUrl || !!error}
            title="Restaurar zoom"
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </div>

        <div className="bg-muted/30 h-[70vh] relative overflow-auto">
          {loading && (
            <div className="absolute inset-0 grid place-items-center" data-testid="portal-receipt-viewer-loading">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando recibo…
              </div>
            </div>
          )}
          {!loading && error && (
            <div className="absolute inset-0 grid place-items-center px-6 text-center">
              <p className="text-sm text-rose-600">{error}</p>
            </div>
          )}
          {!loading && !error && blobUrl && (
            <div
              className="min-w-full min-h-full flex items-start justify-center p-4"
              style={{ minHeight: `${Math.max(70 * zoom, 70)}vh` }}
            >
              <iframe
                ref={iframeRef}
                title={`Recibo ${receipt.receipt_number}`}
                src={blobUrl}
                className="bg-white shadow-sm origin-top"
                style={{
                  width: "100%",
                  height: `${70 * zoom}vh`,
                  minWidth: `${100 * zoom}%`,
                  transform: `scale(1)`,
                }}
                data-testid="portal-receipt-viewer-frame"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-white">
          <Button
            variant="outline" size="sm"
            onClick={openInNewTab}
            disabled={!blobUrl || !!error}
            data-testid="portal-receipt-newtab"
          >
            <ExternalLink className="h-4 w-4 mr-1" /> Abrir em nova aba
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={doPrint}
            disabled={!blobUrl || !!error}
            data-testid="portal-receipt-print"
          >
            <Printer className="h-4 w-4 mr-1" /> Imprimir
          </Button>
          <Button
            size="sm"
            onClick={() => downloadReceipt(receipt)}
            data-testid="portal-receipt-download"
          >
            <Download className="h-4 w-4 mr-1" /> Baixar PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
